import { create } from 'zustand'
import { postRequest } from '../api/requests'
import { LogoutEndpoint } from '../common/endpoints'
import { Role } from '../common/enums'
import LocalStorageSvc, { persistUser, USER_DATA_KEY, UserType } from '../common/localStorageSvc'
import { isStaff } from '../common/typeAssertionFunctions'
import { refreshTokenRequest } from '../requestManager'

type LoginModalMode = 'login' | 'register';
type AuthType = {
    currentUser: UserType | null
    role: Role | null
    showAccountModal: LoginModalMode | null;
    isSuspended: boolean;
    isAdmin: () => boolean;
    isGlobalAdmin: () => boolean;
    isSiteAdmin: () => boolean;
    isStaff: () => boolean;
    isCustomer: () => boolean;
    isLoggedIn: () => boolean;
    setShowAccountModal: (show: LoginModalMode | null) => void;
    refreshToken: () => Promise<boolean>;
    init: () => void;
    setSuspended: () => void;
    setUserData: (user: UserType) => void;
    clearUserData: () => void;
}

const useAuthStore = create<AuthType>((set, get) => {
    const roles = (jwt: string) => {
        const payload = jwt.split('.')[1]
        const decoded = JSON.parse(atob(payload))
        const roleKey = Object.keys(decoded).find(f => f.endsWith('claims/role'))
        return roleKey ? decoded[roleKey] : null
    }

    const isCustomerSuspended = (user: UserType): boolean => {
        if (isStaff(user)) {
            return false
        }
        return !user.isActive
    }

    const setUserData = (user: UserType) => {
        set({ currentUser: user, isSuspended: isCustomerSuspended(user), role: roles(user.jwtToken) })
        persistUser(user)
    }
    const clearUserData = () => {
        set({ currentUser: null, role: null })
        persistUser(null)
    }

    return {
        currentUser: null,
        role: null,
        showAccountModal: null,
        isSuspended: false,

        isAdmin: () => get().role === Role.Admin || get().role === Role.SiteAdmin,
        isGlobalAdmin: () => get().role === Role.Admin,
        isSiteAdmin: () => get().role === Role.SiteAdmin,
        isStaff: () => get().role === Role.Admin || get().role === Role.SiteAdmin || get().role === Role.General,
        isCustomer: () => get().role === Role.Customer,
        isLoggedIn: () => !!get().currentUser,

        setShowAccountModal: (show: LoginModalMode | null): void => {
            set({ showAccountModal: show })
        },

        setUserData,
        clearUserData,

        init: (): void => {
            const loadedUser = LocalStorageSvc.load<UserType>(USER_DATA_KEY)
            if (loadedUser) {
                setUserData(loadedUser)
            } else {
                clearUserData()
            }
        },

        refreshToken: async (): Promise<boolean> => {
            const { currentUser, clearUserData } = get()
            if (!currentUser) {
                return true
            }
            const { refreshToken } = currentUser
            if (!refreshToken) {
                clearUserData()
                await postRequest(LogoutEndpoint, {}).catch(() => {});
                throw new Error('your session has expired, please login again');
            }
            try {
                const result = await refreshTokenRequest(refreshToken)
                if ((result.status === 200 || result.status === 201) && result.data) {
                    const loadedUser = LocalStorageSvc.load<UserType>(USER_DATA_KEY)
                    if (loadedUser) {
                        const clone = { ...loadedUser }
                        // Type assertion to bypass any missing properties
                        const data = result.data as any;
                        clone.jwtToken = data.jwtToken
                        clone.refreshToken = data.refreshToken
                        if (!isStaff(clone)) {
                            clone.isActive = data.isActive
                        }
                        setUserData(clone)
                        return true
                    }
                }
            } catch (err) {
                console.error(err)
                const msg = (err as { message?: string })?.message || 'An error occurred'
                console.error(msg)
                throw new Error(msg);
            }
            clearUserData()
            await postRequest(LogoutEndpoint, {}).catch(() => {});
            throw new Error('your session has expired, please login again');
        },

        setSuspended: () => {
            const { currentUser } = get()
            if (currentUser && !isStaff(currentUser)) {
                setUserData({ ...currentUser, isActive: false })
            }
        }
    }
})

export default useAuthStore
